import { useEffect, useState, type ReactNode } from "react";
import { LockKeyhole, LogOut, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { claimAccountSession } from "@/lib/auth/accountSession";
import { Button } from "@/components/ui/button";
import AccountSessionRecovery from "@/components/AccountSessionRecovery";

const getSessionIdentity = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.sub === "string" && typeof payload.session_id === "string"
      ? JSON.stringify([payload.sub, payload.session_id]) : null;
  } catch { return null; }
};

export default function AccountSessionGate({ children }: { children: ReactNode }) {
  const { session, loading, signOut } = useAuth();
  const [state, setState] = useState<"checking" | "owned" | "blocked" | "unavailable">("checking");
  const [attempt, setAttempt] = useState(0);
  const [validatedToken, setValidatedToken] = useState<string | null>(null);
  const token = session?.access_token ?? null;
  const identity = getSessionIdentity(token);
  const validatedIdentity = getSessionIdentity(validatedToken);
  useEffect(() => {
    if (!token) return;
    let active = true;
    let pending = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const check = async () => {
      if (!active || pending) return;
      pending = true;
      // Missing provider session identity never grants access (including preview tokens).
      try {
        const next = identity ? await Promise.race([
          claimAccountSession(),
          new Promise<"unavailable">((resolve) => {
            timeout = setTimeout(() => resolve("unavailable"), 10_000);
          }),
        ]) : "unavailable";
        if (active) { setValidatedToken(token); setState(next); }
      } catch {
        if (active) { setValidatedToken(token); setState("unavailable"); }
      } finally {
        clearTimeout(timeout);
        pending = false;
      }
    };
    void check();
    const recheck = () => { if (document.visibilityState === "visible") void check(); };
    window.addEventListener("online", recheck);
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => { active = false; clearTimeout(timeout); window.removeEventListener("online", recheck); window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); };
  }, [token, identity, attempt]);
  // Only initial/new identities wait: a refresh of an already server-approved
  // session must not unmount the current screen or restart its form state.
  if (state === "owned" && identity !== null && validatedIdentity === identity) return <>{children}</>;
  if (loading) return <div role="status" className="min-h-dvh bg-background text-foreground flex items-center justify-center">Verificando acesso…</div>;
  // Token rotation is not a new login: retain the mounted tree for the same
  // authorized identity while rechecking. Server guards still protect requests.
  if (!token) return <>{children}</>;
  if ((validatedToken !== token && !(state === "blocked" && identity !== null && validatedIdentity === identity)) || state === "checking") return <div role="status" className="min-h-dvh bg-background text-foreground flex items-center justify-center">Verificando acesso…</div>;
  return <main className="min-h-dvh bg-background text-foreground flex items-center justify-center px-6">
    <section role="alert" className="max-w-sm space-y-5 text-center">
      <LockKeyhole className="mx-auto size-10 text-primary" aria-hidden="true" />
      <h1 className="text-xl font-display font-semibold">{state === "blocked" ? "Acesso bloqueado: conta já conectada" : "Não foi possível verificar o acesso"}</h1>
      <p className="text-sm text-muted-foreground">{state === "blocked" ? "Sua conta já está conectada em outro navegador ou aplicativo, inclusive no modo anônimo. Continue no acesso anterior ou toque em Sair da conta nele. Depois, toque em Tentar novamente aqui." : "Confira sua conexão e tente novamente. Se este acesso veio da prévia ou de uma sessão antiga, saia deste acesso e entre novamente com sua conta."}</p>
      {state === "blocked" && <p className="text-sm text-muted-foreground">Fechar a aba não encerra a sessão da conta. Esta tentativa não desconecta o acesso anterior.</p>}
      <div className="flex flex-col gap-3">
        {state === "blocked" && <AccountSessionRecovery key={identity} onRecovered={() => { setState("checking"); setAttempt(n => n + 1); }} />}
        <Button onClick={() => { setState("checking"); setAttempt(n => n + 1); }}><RefreshCw />Tentar novamente</Button>
        <Button variant="outline" onClick={async () => { const destination = await signOut(); window.location.replace(destination); }}><LogOut />Sair deste acesso</Button>
      </div>
    </section>
  </main>;
}